import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { proxyService } from '../services/proxyService';
import type { AIProvider, AIModel, ConnectionStatus, GenerationParams } from '../types';

interface AIState {
  // Provider & Model selection
  provider: AIProvider;
  model: string;
  fallbackProviders: AIProvider[];

  // Features
  streaming: boolean;
  thinkingMode: boolean;
  thinkingBudget: number;

  // Generation parameters (clinical defaults: low temp, high cap)
  generationParams: GenerationParams;

  // Connection status per provider
  connectionStatus: Record<AIProvider, ConnectionStatus>;

  // Available models (fetched from API)
  availableModels: Record<AIProvider, AIModel[]>;

  // Actions
  setProvider: (provider: AIProvider) => void;
  setModel: (model: string) => void;
  setStreaming: (enabled: boolean) => void;
  setThinkingMode: (enabled: boolean) => void;
  setThinkingBudget: (budget: number) => void;
  setGenerationParam: <K extends keyof GenerationParams>(key: K, value: GenerationParams[K]) => void;
  resetGenerationParams: () => void;
  setConnectionStatus: (provider: AIProvider, status: ConnectionStatus) => void;
  setAvailableModels: (provider: AIProvider, models: AIModel[]) => void;
  setFallbackProviders: (providers: AIProvider[]) => void;
  getNextFallback: (current: AIProvider) => AIProvider | null;
  testConnection: (provider: AIProvider) => Promise<{ connected: boolean; error?: string }>;
}

const DEFAULT_GENERATION_PARAMS: GenerationParams = {
  temperature: 0.3,
  topP: 0.9,
  maxTokens: 6000,
};

// Default models for each provider
const defaultModels: Record<AIProvider, AIModel[]> = {
  openrouter: [
    { id: 'openai/gpt-4o-mini', name: 'GPT-4o Mini', provider: 'openrouter' },
    { id: 'anthropic/claude-3-5-sonnet', name: 'Claude 3.5 Sonnet', provider: 'openrouter' },
    { id: 'meta-llama/llama-3.3-70b-instruct', name: 'Llama 3.3 70B', provider: 'openrouter' },
  ],
  gemini: [
    { id: 'gemini-2.5-flash', name: 'Gemini 2.5 Flash', provider: 'gemini' },
    { id: 'gemini-2.5-pro', name: 'Gemini 2.5 Pro', provider: 'gemini' },
    { id: 'gemini-2.5-flash-lite', name: 'Gemini 2.5 Flash Lite', provider: 'gemini' },
  ],
  grok: [
    { id: 'grok-3', name: 'Grok 3', provider: 'grok' },
    { id: 'grok-3-mini', name: 'Grok 3 Mini', provider: 'grok' },
  ],
  groq: [
    { id: 'llama-3.3-70b-versatile', name: 'Llama 3.3 70B Versatile', provider: 'groq' },
    { id: 'llama-3.1-8b-instant', name: 'Llama 3.1 8B Instant', provider: 'groq' },
    { id: 'mixtral-8x7b-32768', name: 'Mixtral 8x7B', provider: 'groq' },
  ],
  cerebras: [
    { id: 'llama-3.3-70b', name: 'Llama 3.3 70B', provider: 'cerebras' },
    { id: 'llama-3.1-8b', name: 'Llama 3.1 8B', provider: 'cerebras' },
  ],
};

const defaultConnectionStatus: Record<AIProvider, ConnectionStatus> = {
  openrouter: 'disconnected',
  gemini: 'disconnected',
  grok: 'disconnected',
  groq: 'disconnected',
  cerebras: 'disconnected',
};

export const useAIStore = create<AIState>()(
  persist(
    (set, get) => ({
      // Default to Gemini — OpenRouter's free models are persistently rate-limited (429)
      provider: 'gemini',
      model: 'gemini-2.5-flash',
      fallbackProviders: ['groq', 'cerebras', 'gemini'],
      streaming: true,
      thinkingMode: false,
      thinkingBudget: 10000,
      generationParams: { ...DEFAULT_GENERATION_PARAMS },
      connectionStatus: { ...defaultConnectionStatus },
      availableModels: { ...defaultModels },

      setProvider: (provider) => {
        const models = get().availableModels[provider];
        const defaultModel = models[0]?.id || '';
        set({ provider, model: defaultModel });
      },

      setModel: (model) => set({ model }),

      setStreaming: (streaming) => set({ streaming }),

      setThinkingMode: (thinkingMode) => set({ thinkingMode }),

      setThinkingBudget: (thinkingBudget) => set({ thinkingBudget }),

      setGenerationParam: (key, value) =>
        set((state) => ({
          generationParams: { ...state.generationParams, [key]: value },
        })),

      resetGenerationParams: () =>
        set({ generationParams: { ...DEFAULT_GENERATION_PARAMS } }),

      setConnectionStatus: (provider, status) =>
        set((state) => ({
          connectionStatus: { ...state.connectionStatus, [provider]: status },
        })),

      setAvailableModels: (provider, models) =>
        set((state) => ({
          availableModels: { ...state.availableModels, [provider]: models },
        })),

      setFallbackProviders: (fallbackProviders) => set({ fallbackProviders }),

      getNextFallback: (current) => {
        const { fallbackProviders } = get();
        const currentIndex = fallbackProviders.indexOf(current);
        if (currentIndex === -1) {
          return fallbackProviders[0] || null;
        }
        return fallbackProviders[currentIndex + 1] || null;
      },

      testConnection: async (provider) => {
        get().setConnectionStatus(provider, 'testing');
        try {
          const result = await proxyService.testConnection(provider);
          get().setConnectionStatus(
            provider,
            result.connected ? 'connected' : 'error'
          );
          return result;
        } catch (error) {
          get().setConnectionStatus(provider, 'error');
          return {
            connected: false,
            error: error instanceof Error ? error.message : 'Connection failed',
          };
        }
      },
    }),
    {
      name: 'wiscv-ai',
      storage: createJSONStorage(() => localStorage),
      version: 1,
      migrate: (persistedState, version) => {
        const state = persistedState as Partial<AIState>;
        if (version < 1) {
          // Users who never changed the old defaults (openrouter + gpt-4o-mini)
          // are stuck on a provider that permanently returns 429 — move them
          // to Gemini. Deliberate, non-default choices are left untouched.
          if (state.provider === 'openrouter' && state.model === 'openai/gpt-4o-mini') {
            state.provider = 'gemini';
            state.model = 'gemini-2.5-flash';
          }
        }
        return state;
      },
      partialize: (state) => ({
        provider: state.provider,
        model: state.model,
        streaming: state.streaming,
        thinkingMode: state.thinkingMode,
        thinkingBudget: state.thinkingBudget,
        generationParams: state.generationParams,
        fallbackProviders: state.fallbackProviders,
      }),
    }
  )
);
